import type { Citation, WorkEntry } from "./types";

/** At most this many knowledge-image cards under one answer: more crowd out
 *  the reply, and each one is an image fetch. */
export const MAX_IMAGE_CARDS = 3;

type Media = NonNullable<Citation["media"]>;

/** Where a picture sits in its document, as a stable string ("" for none). */
function locatorKey(locator: Media["locator"]): string {
  if (!locator) return "";
  if ("part" in locator) return `part:${locator.part}`;
  return `page:${locator.page}:${locator.box.join(",")}`;
}

/** Two hits on the same picture region are one card. Ids are stringified so a
 *  history row ("7") and a live frame (7) collapse together. A picture inside
 *  a PDF/DOCX/PPTX adds its index and locator, so a guide's screenshots are
 *  separate cards; keys of standalone images are unchanged (`doc|crop`). */
export function mediaKey(media: Media): string {
  const base = `${String(media.document_id)}|${media.crop ? media.crop.join(",") : ""}`;
  if (media.picture == null && !media.locator) return base;
  return `${base}|${media.picture ?? ""}|${locatorKey(media.locator)}`;
}

/** The key the turn reducer dedupes citations by: the url, except that a
 *  picture inside a document is cited on its own (mirrors the backend's
 *  `kb_citation_key`), so pictures of one document — which share its url —
 *  aren't merged into one. `undefined` (no url) means "never deduped". */
export function citationDedupeKey(c: Citation): string | undefined {
  if (!c.url) return undefined;
  const picture = c.media?.picture;
  return picture == null ? c.url : `${c.url}#picture=${picture}`;
}

/** The turn's image-bearing citations, in citation order: the turn's own
 *  citations first, then each tool step's results. Deduplicated by
 *  document + crop and capped at {@link MAX_IMAGE_CARDS}. */
export function mediaCitations(
  citations: Citation[] | undefined,
  workLog: WorkEntry[] | undefined,
): Citation[] {
  const all: Citation[] = [...(citations ?? [])];
  for (const entry of workLog ?? []) {
    if (entry.kind === "tool" && entry.results) all.push(...entry.results);
  }
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const c of all) {
    if (!c.media) continue;
    const key = mediaKey(c.media);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
    if (out.length >= MAX_IMAGE_CARDS) break;
  }
  return out;
}
